import * as RequestBody from "./request-body"
import { toApiDateFormat } from "./utils"
import { Role } from "../../constant/role"
import { Profile } from "../../domain/sfp/user-domain"
import * as Model from "../../model"

export function createAccountRequestBody(account: Model.Account): RequestBody.AccountRequestBody {
    const requestBody: RequestBody.AccountRequestBody = {
        account: {
            class: account.class.id,
            expense: account.expense,
            label: account.label,
            number: account.number,
            operation: account.operation,
            receipts: account.receipts,
            template: account.template
        }
    }

    if (account.addedOn) requestBody.account.addedOn = toApiDateFormat(account.addedOn)
    if (account.removedOn) requestBody.account.removedOn = toApiDateFormat(account.removedOn)
    if (account.defaultChapter) requestBody.account.defaultChapter = account.defaultChapter.id

    return requestBody
}

export function createBergerLevraultRequestBody(bl: Model.BergerLevrault): RequestBody.CreateBergerLevraultRequestBody {
    const requestBody: RequestBody.CreateBergerLevraultRequestBody = {
        bergerLevrault: {
            enabled: bl.enabled,
            idCollectiviteGF: bl.idCollectiviteGF,
        }
    }
    return requestBody
}

export function createDemographyRequestBody(demography: Model.Demography): RequestBody.CreateDemographyRequestBody {
    const requestBody: RequestBody.CreateDemographyRequestBody = {
        population: demography.population,
        year: demography.year
    }
    return requestBody
}

export function createDemographyUpdateRequestBody(demography: Model.Demography): RequestBody.UpdateDemographyRequestBody {
    const requestBody: RequestBody.UpdateDemographyRequestBody = {
        demography: {
            population: demography.population,
            year: demography.year
        }
    }
    return requestBody
}

export function createLacaUpdateRequestBody(laca: Model.LocalAuthorityChapterAccount): RequestBody.UpdateLacaRequestBody {
    const requestBody: RequestBody.UpdateLacaRequestBody = {
        laca: {
            account: {
                expense: laca.account.expense,
                label: laca.account.label,
                number: laca.account.number,
                operation: laca.account.operation,
                receipts: laca.account.receipts,
                template: laca.account.template
            },
            chapter: laca.chapter.id,
        }
    }
    return requestBody
}

export function createLocalAuthorityRequestBody(
    name: string,
    code: string,
    instruction: Model.Instruction,
    bergerLevrault?: Model.BergerLevrault,
    politicalApproach?: boolean,
    monthlyCountersFillingReminder?: boolean,
    notificationAfterCountersImport?: boolean
): RequestBody.CreateLocalAuthorityRequestBody {
    const requestBody: RequestBody.CreateLocalAuthorityRequestBody = {
        localAuthority: {
            code,
            name,
            politicalApproach,
            instruction: instruction.id,
            monthlyCountersFillingReminder: monthlyCountersFillingReminder || false,
            notificationAfterCountersImport: notificationAfterCountersImport || false
        }
    }
    if (bergerLevrault) {
        requestBody.localAuthority.bergerLevrault = {
            axe: bergerLevrault.axe,
            budget: bergerLevrault.budget,
            idAxe: bergerLevrault.idAxe,
            enabled: bergerLevrault.enabled,
            idCollectiviteGF: bergerLevrault.idCollectiviteGF,
            port: bergerLevrault.port,
            server: bergerLevrault.server
        }
    }
    return requestBody
}


export function createLocalAuthorityUpdateRequestBody(
    name: string,
    code: string,
    bergerLevrault?: Model.BergerLevrault,
    politicalApproach?: boolean,
    monthlyCountersFillingReminder?: boolean,
    notificationAfterCountersImport?: boolean
): RequestBody.CreateLocalAuthorityUpdateRequestBody {
    const requestBody: RequestBody.CreateLocalAuthorityUpdateRequestBody = {
        localAuthority: {
            code,
            name,
            politicalApproach,
            monthlyCountersFillingReminder: monthlyCountersFillingReminder || false,
            notificationAfterCountersImport: notificationAfterCountersImport || false
        }
    }
    if (bergerLevrault) {
        requestBody.localAuthority.bergerLevrault = {
            axe: bergerLevrault.axe,
            budget: bergerLevrault.budget,
            idAxe: bergerLevrault.idAxe,
            enabled: bergerLevrault.enabled,
            idCollectiviteGF: bergerLevrault.idCollectiviteGF,
            port: bergerLevrault.port,
            server: bergerLevrault.server
        }
    } else {
        requestBody.localAuthority.bergerLevrault = null
    }
    return requestBody
}

export function createInterestVariationRequestBody(
    interestVariation: Model.InterestVariation
): RequestBody.InterestVariationRequestBody {
    return {
        interest: interestVariation.interest,
        paymentDateIndex: interestVariation.paymentDateIndex
    }
}

export function createLoanRequestBody(
    loan: Model.Loan
): RequestBody.LoanRequestBody {
    const localAuthority = loan.localAuthority.id
    const requestBody: RequestBody.LoanRequestBody = {
        loan: {
            amount: loan.amount,
            constantAmortization: loan.constantAmortization,
            contractedOn: toApiDateFormat(loan.contractedOn),
            duration: loan.duration,
            interestVariations: loan.interestVariations.map(createInterestVariationRequestBody),
            number: loan.number,
            periodicity: loan.periodicity,
            rate: loan.rate,
            rateVariations: loan.rateVariations.map(createRateVariationRequestBody),
            reimboursedCapitalVariations: loan.reimboursedCapitalVariations.map(createReimboursedCapitalVariationRequestBody),
            simulate: loan.simulate,
            subject: loan.subject,
            variableRate: loan.variableRate,
            detteRecuperable: loan.detteRecuperable
        }
    }

    if (!loan.simulate) {
        if (loan.bank.id) {
            requestBody.loan.bank = loan.bank.id
        } else {
            requestBody.loan.newBank = {
                label: loan.bank.label,
                localAuthority
            }
        }
    }

    return requestBody
}

export function createServiceGroupRequestBody(
    localAuthorityId: number,
    label: string
): RequestBody.ServiceGroupRequestBody {
    const requestBody: RequestBody.ServiceGroupRequestBody = {
        serviceGroup: {
            label: label,
            localAuthority: localAuthorityId
        }
    }
    return requestBody
}

export function createScenarioAccountThemeCommentRequestBody(
    account: Model.Account,
    theme: Model.Theme,
    comment: string,
    receipts: boolean
): RequestBody.ScenarioAccountThemeRequestBody {
    const requestBody: RequestBody.ScenarioAccountThemeRequestBody = {
        account: account.id,
        theme: `${theme.localAuthority}|${theme.slug}`,
        comment,
        receipts
    }
    return requestBody
}

export function createObservationRequestBody(
    observation: Model.Observation
): RequestBody.ObservationRequestBody {
    const requestBody: RequestBody.ObservationRequestBody = {
        observation: {
            account: observation.account.id,
            amount: observation.amount,
            receipts: observation.receipts,
            year: observation.year,
        }
    }
    return requestBody
}

export function createUpdateObservationRequestBody(observation: Model.Observation): RequestBody.UpdateObservationRequestBody {
    const requestBody: RequestBody.UpdateObservationRequestBody = {
        observation: {
            amount: observation.amount
        }
    }
    return requestBody
}

export function createRateVariationRequestBody(
    rateVariation: Model.RateVariation
): RequestBody.RateVariationRequestBody {
    return {
        paymentDateIndex: rateVariation.paymentDateIndex,
        rate: rateVariation.rate
    }
}

export function createReimboursedCapitalVariationRequestBody(
    reimboursedCapitalVariation: Model.ReimboursedCapitalVariation
): RequestBody.ReimboursedCapitalVariationRequestBody {
    return {
        paymentDateIndex: reimboursedCapitalVariation.paymentDateIndex,
        reimboursedCapital: reimboursedCapitalVariation.reimboursedCapital,
    }
}

export function createLacaRequestBody(laca: Model.LocalAuthorityChapterAccount): RequestBody.LacaCreateRequestBody {
    const requestBody: RequestBody.LacaCreateRequestBody = {
        laca: {
            account: {
                addedOn: null,
                class: laca.account.class.id,
                defaultChapter: null,
                expense: laca.account.expense,
                label: laca.account.label,
                number: laca.account.number,
                operation: laca.account.operation,
                receipts: laca.account.receipts,
                removedOn: null,
                template: laca.account.template,
            },
            chapter: laca.chapter.id
        }
    }
    return requestBody
}

export function createServiceRequestBody(
    label: string,
    code: string,
    serviceGroup: number,
    description?: string,
    approaches?: any
): RequestBody.ServiceRequestBody {
    const requestBody: RequestBody.ServiceRequestBody = { service: { label, code, serviceGroup, approaches } }

    if (description) requestBody.service.description = description

    return requestBody
}

export function createUserBaseRequestBody(
    title: Model.Title,
    firstName: string,
    lastName: string,
    email: string
): RequestBody.UserRequestBody {
    return {
        user: {
            email,
            firstName,
            lastName,
            roles: [],
            title: title.id
        }
    }
}

export function createRegionBaseRequestBody(
    name: string,
    localAuthorities: any,
    users: any
): RequestBody.RegionRequestBody {
    return {
        region: {
            name,
            localAuthorities,
            users
        }
    }
}

export function createRegionRequestBody(
    region?: string,
    localAuthority?: any,
    users?: any
): RequestBody.RegionRequestBody {
    let requestBody: RequestBody.RegionRequestBody
    requestBody = createRegionBaseRequestBody(
        region,
        localAuthority,
        users
    )
    return requestBody
}

export function createUserRequestBody(
    title: Model.Title,
    firstName: string,
    lastName: string,
    email: string,
    profile: Profile,
    phone?: string,
    region?: Model.Region,
    localAuthority?: Model.LocalAuthority,
    services?: Model.Service[],
    managedBudgetAnnexes?: number[],
    consultedBudgetAnnexes?: number[],
    serviceBudgetAnnexes?: number[]
): RequestBody.UserRequestBody {
    let requestBody: RequestBody.UserRequestBody

    switch (profile) {
        case Profile.SuperAdmin:
            requestBody = createUserSuperAdminRequestBody(
                title,
                firstName,
                lastName,
                email
            )
            break
        case Profile.RegionAdmin:
            requestBody = createUserRegionAdminRequestBody(
                title,
                firstName,
                lastName,
                email,
                region
            )
            break
        case Profile.LocalAuthorityAdmin:
            requestBody = createUserLocalAuthorityAdminRequestBody(
                title,
                firstName,
                lastName,
                email,
                localAuthority
            )
            break
        case Profile.LocalAuthorityConsultant:
            requestBody = createUserLocalAuthorityConsultantRequestBody(
                title,
                firstName,
                lastName,
                email,
                localAuthority
            )
            break
        case Profile.ServiceAgent:
            requestBody = createUserServiceAgentRequestBody(
                title,
                firstName,
                lastName,
                email,
                services,
                serviceBudgetAnnexes
            )
            break
        case Profile.Adminbudgetannexe:
            requestBody = createUserAdminbudgetannexeBody(
                title,
                firstName,
                lastName,
                email,
                managedBudgetAnnexes
            )
            break
        case Profile.Consultantbudgeannexe:
            requestBody = createUserConsultantbudgeannexeBody(
                title,
                firstName,
                lastName,
                email,
                consultedBudgetAnnexes
            )
            break
    }

    if (phone && requestBody.user) requestBody.user.phone1 = phone

    return requestBody
}

export function createUserSuperAdminRequestBody(
    title: Model.Title,
    firstName: string,
    lastName: string,
    email: string
): RequestBody.UserRequestBody {
    const requestBody = createUserBaseRequestBody(
        title,
        firstName,
        lastName,
        email
    )

    // add super admin role
    requestBody.user.roles.push(Role.SuperAdmin)
    // remove managed region
    //requestBody.user.managedRegion = null
    // remove managed local authority
    requestBody.user.managedLocalAuthority = null
    // remove consulted local authority
    requestBody.user.consultedLocalAuthority = null
    // empty services
    requestBody.user.services = []

    return requestBody
}

export function createUserRegionAdminRequestBody(
    title: Model.Title,
    firstName: string,
    lastName: string,
    email: string,
    region: Model.Region
): RequestBody.UserRequestBody {
    const requestBody = createUserBaseRequestBody(
        title,
        firstName,
        lastName,
        email
    )

    // add region
    requestBody.user.region = region.id
    // empty roles
    // requestBody.user.roles = []
    // // remove managed local authority
    // requestBody.user.managedLocalAuthority = null
    // // remove consulted local authority
    // requestBody.user.consultedLocalAuthority = null
    // // empty services
    // requestBody.user.services = []

    return requestBody
}

export function createUserLocalAuthorityAdminRequestBody(
    title: Model.Title,
    firstName: string,
    lastName: string,
    email: string,
    localAuthority: Model.LocalAuthority
): RequestBody.UserRequestBody {
    const requestBody = createUserBaseRequestBody(
        title,
        firstName,
        lastName,
        email
    )

    // add managed local authority
    requestBody.user.managedLocalAuthority = localAuthority.id
    // empty roles
    requestBody.user.roles = []
    // remove region
    //requestBody.user.managedRegion = null
    // remove consulted local authority
    requestBody.user.consultedLocalAuthority = null
    // empty services
    requestBody.user.services = []

    return requestBody
}

export function createUserLocalAuthorityConsultantRequestBody(
    title: Model.Title,
    firstName: string,
    lastName: string,
    email: string,
    localAuthority: Model.LocalAuthority
): RequestBody.UserRequestBody {
    const requestBody = createUserBaseRequestBody(
        title,
        firstName,
        lastName,
        email
    )

    // add consulted local authority
    requestBody.user.consultedLocalAuthority = localAuthority.id
    // empty roles
    requestBody.user.roles = []
    // remove region
    //requestBody.user.managedRegion = null
    // remove managed local authority
    requestBody.user.managedLocalAuthority = null
    // empty services
    requestBody.user.services = []

    return requestBody
}

export function createUserAdminbudgetannexeBody(
    title: Model.Title,
    firstName: string,
    lastName: string,
    email: string,
    managedBudgetAnnexes: number[]
): RequestBody.UserRequestBody {
    const requestBody = createUserBaseRequestBody(
        title,
        firstName,
        lastName,
        email
    )


    requestBody.user.managedBudgetAnnexes = managedBudgetAnnexes


    return requestBody
}

export function createUserConsultantbudgeannexeBody(
    title: Model.Title,
    firstName: string,
    lastName: string,
    email: string,
    consultedBudgetAnnexes: number[]
): RequestBody.UserRequestBody {
    const requestBody = createUserBaseRequestBody(
        title,
        firstName,
        lastName,
        email
    )
    requestBody.user.consultedBudgetAnnexes = consultedBudgetAnnexes

    return requestBody
}


export function createUserServiceAgentRequestBody(
    title: Model.Title,
    firstName: string,
    lastName: string,
    email: string,
    services?: Model.Service[],
    serviceBudgetAnnexes?: number[]
): RequestBody.UserRequestBody {
    const requestBody = createUserBaseRequestBody(
        title,
        firstName,
        lastName,
        email
    )

    // add services
    requestBody.user.services = services.map(s => s && s.id)

    requestBody.user.serviceBudgetAnnexes = serviceBudgetAnnexes
    // empty roles
    requestBody.user.roles = []
    // remove region
    //requestBody.user.managedRegion = null
    // remove managed local authority
    requestBody.user.managedLocalAuthority = null
    // remove consulted local authority
    requestBody.user.consultedLocalAuthority = null

    return requestBody
}

export function createPolitiqueRequestBody(word: Model.Word) {
    const requestBody: RequestBody.CreatePolitiqueRequestBody = {
        politicalApproach: {
            name: word.Politique,
        }
    }
    return requestBody
}

export function createWordRequestBody(word: Model.Word) {
    const requestBody: RequestBody.CreateWordRequestBody = {
        word: {
            term: word.term,
            definition: word.definition,
        }
    }
    return requestBody
}

export function createFileUploadRequestBody(
    dataFile: File,
): RequestBody.createFileUploadRequestBody {
    const requestBody: RequestBody.createFileUploadRequestBody = {
        file: dataFile
    }
    return requestBody
}

export function createCommentUpdateRequestBody(comment: string
): RequestBody.createCommentUpdateRequestBody {
    const requestBody: RequestBody.createCommentUpdateRequestBody = {
        scenario_year_account: { comment: comment }
    }
    return requestBody
}

export function createFileUpdateRB(
    name: string,
): RequestBody.fileUpdateRB {
    const requestBody: RequestBody.fileUpdateRB = {
        s3_file: { title: name }
    }
    return requestBody
}


// WEBPACK FOOTER //
// ./src/service/sfp/factory.ts